import {
  completeCheckIn as completeCheckInUseCase,
  copyMeal as copyMealUseCase,
  createRemoteBackup,
  deleteDiaryEntry as deleteDiaryEntryUseCase,
  deleteRecipe as deleteRecipeUseCase,
  getWeightChart,
  logFood as logFoodUseCase,
  logRecipe as logRecipeUseCase,
  logWeight as logWeightUseCase,
  prepareCheckIn,
  replaceWeekAllocations,
  restoreLatestRemoteBackup,
  rankFoodSearchResults,
  saveCustomFood as saveCustomFoodUseCase,
  saveImportedFood,
  saveRecipe as saveRecipeUseCase,
  scanAndLookUpBarcode,
  searchFoods as searchFoodsUseCase,
  startTracking,
  updateDiaryEntry as updateDiaryEntryUseCase,
  type BarcodeScanner,
  type BackupStore,
  type Clock,
  type FoodSource,
  type IdGenerator,
  type LocalBackupGateway,
  type PreparedCheckIn,
} from "../application";
import {
  addCalendarDays,
  alignedWeekStart,
  calculateDailyCalorieFloor,
  calculateDailyPlanProgress,
  caloriesFromNutrients,
  checkInStateOn,
  createDailyNutrientTarget,
  fiveCalendarDayWeightTrend,
  getNutrientDefinition,
  isMassNutrientUnit,
  NUTRIENT_KEY_MAX_LENGTH,
  NUTRIENT_NAME_MAX_LENGTH,
  normalizeMealName,
  normalizeNutrientUnit as normalizeCanonicalNutrientUnit,
  perServingRecipeNutrients,
  amountInPreferredUnit,
  resolveNutrient,
  summarizeDiary,
  createImportedFoodDraft,
  validateWeekday,
  type CalendarDate,
  type CheckInQuestionnaire,
  type DailyNutrientTarget,
  type DiaryEntry as DomainDiaryEntry,
  type Food,
  type FoodPortion,
  type GoalPhase,
  type ImportedFoodDraft,
  type NutrientProfile,
  type Recipe,
  type WeightEntry,
  type WeekPlan,
} from "../domain";
import type {
  AppController,
  AppSnapshot,
  CompleteCheckInInput,
  CompleteOnboardingInput,
  CopyMealInput,
  DaySummary,
  FoodDraft,
  FoodPhotoEstimate,
  FoodSearchResult,
  GoalSettings,
  LogFoodInput,
  LogRecipeInput,
  NutrientAmount,
  NutrientGoal,
  NutritionSummary,
  PreviewCheckInInput,
  RecipeDraft,
  RecipeDetails,
  RestoreResult,
  SavePhotoFoodAndLogInput,
  UpdateDiaryEntryInput,
  WeightPoint,
} from "../ui/types";
import {
  USER_DATA_WRITE_LOCK,
  type CrossTabCoordinator,
} from "../infrastructure/browser/cross-tab-coordinator";
import type { BrowserStoragePersistence } from "../infrastructure/browser/storage-persistence";
import type { GoogleDriveAuthorization } from "../infrastructure/google-drive/authorization";
import type { BrowserFoodRecognizer } from "../infrastructure/food-recognition";
import type { DietTrackerDatabase } from "../infrastructure/persistence/indexed-db/database";
import type { DomainRepositoryBundle } from "../infrastructure/persistence/indexed-db/domain-repositories";
import type { IndexedDbConfigurationRepository } from "../infrastructure/persistence/indexed-db/repositories/configuration-repository";
import { createEmptySnapshot, DEFAULT_GOAL_SETTINGS, formatLongDate, weekdayName } from "./default-snapshot";

interface ControllerDependencies {
  database: DietTrackerDatabase;
  repositories: DomainRepositoryBundle;
  configurationRecords: IndexedDbConfigurationRepository;
  clock: Clock;
  ids: IdGenerator;
  foodSources: readonly FoodSource[];
  barcodeScanner: BarcodeScanner;
  foodRecognizer?: Pick<BrowserFoodRecognizer, "recognize">;
  localBackup: LocalBackupGateway;
  remoteBackup?: BackupStore;
  authorization?: GoogleDriveAuthorization;
  storagePersistence: BrowserStoragePersistence;
  coordinator: CrossTabCoordinator;
}

const DEFAULT_QUESTIONNAIRE: CheckInQuestionnaire = Object.freeze({
  loggedEverything: true,
  calorieCompliancePercent: 100,
});

const JOIN_CURRENT_TRANSACTION = { run: <T>(work: () => Promise<T>) => work() };

export class BrowserAppController implements AppController {
  private snapshot: AppSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly pendingImports = new Map<string, ImportedFoodDraft>();
  private readonly detailedImports = new Set<string>();
  private readonly resolvedImports = new Map<string, string>();
  private previewQuestionnaire?: CheckInQuestionnaire;
  private preparedCheckIn?: PreparedCheckIn;
  private backupBusy = false;

  constructor(private readonly dependencies: ControllerDependencies) {
    this.snapshot = createEmptySnapshot(dependencies.clock.today(), onlineStatus());
  }

  readonly getSnapshot = (): AppSnapshot => this.snapshot;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  async initialize(): Promise<void> {
    await this.dependencies.database.open();
    this.dependencies.coordinator.subscribe(() => { void this.refresh(); });
    window.addEventListener("online", this.handleConnectivityChange);
    window.addEventListener("offline", this.handleConnectivityChange);
    await this.refresh();
  }

  async completeOnboarding(input: CompleteOnboardingInput): Promise<void> {
    await this.runMutation(async () => {
      const today = this.dependencies.clock.today();
      const now = this.dependencies.clock.now();
      const checkInDay = validateWeekday(input.goalSettings.checkInDay);
      const planStartDate = alignedWeekStart(today, checkInDay);
      const configuration = await this.dependencies.repositories.configuration.get();
      const updatedConfiguration = withRemainderSplit(configuration, input.goalSettings);
      const goal = goalFromSettings(input.goalSettings, this.dependencies.ids.next(), planStartDate, now);

      await this.dependencies.database.runUserDataTransaction(async () => {
        await this.dependencies.configurationRecords.putProfile({
          id: "primary",
          homeTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          createdAt: now,
          updatedAt: now,
        });
        await this.dependencies.configurationRecords.putSettings({
          id: "settings",
          weightUnit: "kg",
          nominatedCheckInWeekday: checkInDay,
          googleDriveBackupEnabled: false,
          updatedAt: now,
        });
        await this.dependencies.repositories.configuration.save(updatedConfiguration);
        await startTracking(
          {
            goals: this.dependencies.repositories.goals,
            weekPlans: this.dependencies.repositories.weekPlans,
            onboardingReference: this.dependencies.repositories.onboardingReference,
            configuration: this.dependencies.repositories.configuration,
            transaction: JOIN_CURRENT_TRANSACTION,
            clock: this.dependencies.clock,
            ids: this.dependencies.ids,
          },
          {
            goal,
            planStartDate,
            baselineDailyCalories: Math.round(input.baselineCalories),
            referenceWeightKilograms: input.startingWeightKg,
            catchUpThroughDate: planStartDate < today ? addCalendarDays(today, -1) : undefined,
          },
        );
        await logWeightUseCase(
          {
            weights: this.dependencies.repositories.weights,
            weekPlans: this.dependencies.repositories.weekPlans,
            clock: this.dependencies.clock,
            ids: this.dependencies.ids,
          },
          { date: today, kilograms: input.startingWeightKg },
        );
      });
    });

    // Browsers may decline; local tracking remains fully functional either way.
    void this.dependencies.storagePersistence.requestPersistence().catch(() => false);
  }

  async searchFoods(query: string): Promise<FoodSearchResult[]> {
    const recipes = await this.dependencies.repositories.recipes.search(query, 20);
    let foods: FoodSearchResult[];
    try {
      foods = await this.searchFoodItems(query);
    } catch (error) {
      if (recipes.length === 0) throw error;
      foods = [];
    }
    const savedRecipes = recipes.map(recipeResultFromRecipe);
    return rankFoodSearchResults(query, deduplicateFoodResults([...savedRecipes, ...foods])).slice(0, 20);
  }

  async prepareFoodForAdding(result: FoodSearchResult): Promise<FoodSearchResult> {
    if ((result.kind ?? "food") === "recipe") return result;

    const pending = this.pendingImports.get(result.id);
    if (pending) {
      if (this.detailedImports.has(result.id)) return result;
      const detailed = await this.fetchDetailedImport(
        pending.candidate.provider,
        pending.candidate.providerFoodId,
        pending.candidate.barcode,
      );
      if (!detailed) return result;
      this.detailedImports.add(result.id);
      if ((detailed.draft.candidate.measures?.length ?? 0) === 0) return result;
      this.pendingImports.set(result.id, detailed.draft);
      return foodResultFromImport(result.id, detailed.draft, detailed.attribution);
    }

    const food = await this.dependencies.repositories.foods.getById(result.id);
    if (!food || food.provenance.kind !== "imported" || (food.measures?.length ?? 0) > 0) return result;
    const provider = food.provenance.provider;
    const providerFoodId = food.provenance.providerFoodId;
    if (!provider || !providerFoodId) return result;
    const detailed = await this.fetchDetailedImport(provider, providerFoodId, food.barcode);
    if (!detailed || (detailed.draft.candidate.measures?.length ?? 0) === 0) return result;
    const importId = importKey(detailed.draft);
    this.pendingImports.set(importId, detailed.draft);
    this.detailedImports.add(importId);
    this.resolvedImports.delete(importId);
    return foodResultFromImport(importId, detailed.draft, detailed.attribution);
  }

  private async fetchDetailedImport(
    provider: string,
    providerFoodId: string,
    barcode: string | undefined,
  ): Promise<{ draft: ImportedFoodDraft; attribution?: string } | undefined> {
    if (!onlineStatus() || !barcode) return undefined;
    const source = this.dependencies.foodSources.find((candidate) => candidate.id === provider);
    if (!source) return undefined;
    try {
      const result = await source.findByBarcode(barcode);
      if (!result
        || result.candidate.provider !== provider
        || !sameProviderFoodId(provider, providerFoodId, result.candidate.providerFoodId)) return undefined;
      const candidate = result.candidate.providerFoodId === providerFoodId
        ? result.candidate
        : { ...result.candidate, providerFoodId };
      const draft = createImportedFoodDraft(candidate);
      return {
        draft,
        ...(result.attribution === undefined ? {} : { attribution: result.attribution }),
      };
    } catch {
      // Product details improve the unit picker but must never prevent logging
      // the nutrition data already returned by name search.
      return undefined;
    }
  }

  async searchRecipeIngredients(query: string): Promise<FoodSearchResult[]> {
    return this.searchFoodItems(query);
  }

  private async searchFoodItems(query: string): Promise<FoodSearchResult[]> {
    let response: Awaited<ReturnType<typeof searchFoodsUseCase>>;
    try {
      response = await searchFoodsUseCase(
        { foods: this.dependencies.repositories.foods, sources: this.dependencies.foodSources },
        { query, limit: 20, includeOnline: onlineStatus() },
      );
    } catch (error) {
      if (!onlineStatus()) throw error;
      const localFallback = await searchFoodsUseCase(
        { foods: this.dependencies.repositories.foods, sources: [] },
        { query, limit: 20, includeOnline: false },
      );
      if (localFallback.local.length === 0) throw error;
      response = localFallback;
    }
    const local = response.local
      .filter((food) => !response.online.some(({ draft }) => isProviderMeasureRefresh(food, draft)))
      .map((food) => foodResultFromFood(food));
    const online = response.online.map(({ draft, attribution }) => {
      const importId = importKey(draft);
      this.pendingImports.set(importId, draft);
      if (/^\d{6,14}$/.test(query.trim())) this.detailedImports.add(importId);
      else this.detailedImports.delete(importId);
      return foodResultFromImport(importId, draft, attribution);
    });
    return rankFoodSearchResults(query, deduplicateFoodResults([...local, ...online])).slice(0, 20);
  }

  async scanBarcode(): Promise<FoodSearchResult | null> {
    const result = await scanAndLookUpBarcode({
      scanner: this.dependencies.barcodeScanner,
      foods: this.dependencies.repositories.foods,
      sources: onlineStatus() ? this.dependencies.foodSources : [],
    });
    if (result.kind === "not_found") return null;
    if (result.kind === "local") return foodResultFromFood(result.food);
    const id = importKey(result.draft);
    this.pendingImports.set(id, result.draft);
    this.detailedImports.add(id);
    return foodResultFromImport(id, result.draft, result.attribution);
  }

  async recognizeFoodPhoto(photo: File, signal?: AbortSignal): Promise<FoodPhotoEstimate> {
    const recognizer = this.dependencies.foodRecognizer;
    if (!recognizer) throw new Error("Photo food recognition is not available");
    return recognizer.recognize(photo, { signal });
  }

  async saveCustomFood(draft: FoodDraft): Promise<FoodSearchResult> {
    return this.runMutation(async () => {
      const food = await saveCustomFoodUseCase(
        {
          foods: this.dependencies.repositories.foods,
          clock: this.dependencies.clock,
          ids: this.dependencies.ids,
        },
        {
          name: draft.name,
          brand: draft.brand,
          barcode: draft.barcode,
          basis: { amount: draft.basisAmount, unit: draft.basisUnit, servingName: draft.servingName },
          nutrients: nutrientsFromDraft(draft),
        },
      );
      return foodResultFromFood(food);
    });
  }

  async savePhotoFoodAndLog(input: SavePhotoFoodAndLogInput): Promise<FoodSearchResult> {
    return this.runMutation(async () => {
      const food = await this.dependencies.database.runUserDataTransaction(async () => {
        const savedFood = await saveCustomFoodUseCase(
          {
            foods: this.dependencies.repositories.foods,
            clock: this.dependencies.clock,
            ids: this.dependencies.ids,
          },
          {
            name: input.draft.name,
            brand: input.draft.brand,
            barcode: input.draft.barcode,
            basis: {
              amount: input.draft.basisAmount,
              unit: input.draft.basisUnit,
              servingName: input.draft.servingName,
            },
            nutrients: nutrientsFromDraft(input.draft),
            source: "photo-estimate",
          },
        );
        const plan = await requireOpenPlan(this.dependencies.repositories.weekPlans);
        const state = checkInStateOn(plan, this.dependencies.clock.today());
        await logFoodUseCase(
          {
            foods: this.dependencies.repositories.foods,
            diary: this.dependencies.repositories.diary,
            weekPlans: this.dependencies.repositories.weekPlans,
            clock: this.dependencies.clock,
            ids: this.dependencies.ids,
          },
          {
            foodId: savedFood.id,
            date: input.date,
            meal: input.meal,
            portion: portionFromLogQuantity(savedFood, input.quantity),
            intent: state === "overdue_locked" ? "check_in_backfill" : "normal",
          },
        );
        return savedFood;
      });
      return foodResultFromFood(food);
    });
  }

  async confirmImportedFood(draft: FoodDraft): Promise<FoodSearchResult> {
    const pending = draft.importId ? this.pendingImports.get(draft.importId) : undefined;
    if (!pending) throw new Error("This import has expired. Search for the food again before saving it.");
    return this.runMutation(async () => {
      const existingFood = draft.measures && draft.measures.length > 0
        ? await findExistingMeasureRefresh(this.dependencies.repositories.foods, pending, draft.basisUnit)
        : undefined;
      const food = await saveImportedFood(
        {
          foods: this.dependencies.repositories.foods,
          clock: this.dependencies.clock,
          ids: this.dependencies.ids,
        },
        {
          draft: pending,
          finalNutrients: nutrientsFromDraft(draft),
          userReviewedMacros: true,
          existingFood,
          metadata: {
            name: draft.name,
            brand: draft.brand,
            barcode: draft.barcode,
            basis: { amount: draft.basisAmount, unit: draft.basisUnit, servingName: draft.servingName },
            measures: draft.measures,
          },
        },
      );
      this.resolvedImports.set(draft.importId!, food.id);
      this.pendingImports.delete(draft.importId!);
      this.detailedImports.delete(draft.importId!);
      return foodResultFromFood(food);
    });
  }

  async saveRecipe(draft: RecipeDraft): Promise<FoodSearchResult> {
    return this.runMutation(async () => {
      const imported = new Map<string, string>();
      const recipe = await this.dependencies.database.runUserDataTransaction(async () => {
        const ingredients: RecipeDraft["ingredients"] = [];
        for (const ingredient of draft.ingredients) {
          const requestedId = ingredient.foodId;
          let foodId = imported.get(requestedId) ?? this.resolvedImports.get(requestedId) ?? requestedId;
          let food = await this.dependencies.repositories.foods.getById(foodId);
          if (!food) {
            const pending = this.pendingImports.get(requestedId);
            if (!pending) throw new Error(`Food not found: ${requestedId}`);
            if (pending.macroReviewRequired) {
              throw new Error(`Review and save ${pending.candidate.name} as a food before using it in a recipe`);
            }
            const existingFood = await findExistingMeasureRefresh(
              this.dependencies.repositories.foods,
              pending,
              pending.candidate.basis.unit,
            );
            food = await saveImportedFood(
              {
                foods: this.dependencies.repositories.foods,
                clock: this.dependencies.clock,
                ids: this.dependencies.ids,
              },
              {
                draft: pending,
                finalNutrients: pending.proposedNutrients,
                userReviewedMacros: false,
                existingFood,
              },
            );
            foodId = food.id;
            imported.set(requestedId, foodId);
          }
          ingredients.push({ ...ingredient, foodId });
        }

        return saveRecipeUseCase(
          {
            foods: this.dependencies.repositories.foods,
            recipes: this.dependencies.repositories.recipes,
            clock: this.dependencies.clock,
            ids: this.dependencies.ids,
          },
          { ...draft, ingredients },
        );
      });
      for (const [requestedId, savedId] of imported) {
        this.resolvedImports.set(requestedId, savedId);
        this.pendingImports.delete(requestedId);
        this.detailedImports.delete(requestedId);
      }
      return recipeResultFromRecipe(recipe);
    });
  }

  async listRecipes(): Promise<RecipeDetails[]> {
    return (await this.dependencies.repositories.recipes.list()).map(recipeDetailsFromRecipe);
  }

  async deleteRecipe(recipeId: string): Promise<void> {
    await this.runMutation(() => deleteRecipeUseCase(
      { recipes: this.dependencies.repositories.recipes },
      recipeId,
    ));
  }

  async logFood(input: LogFoodInput): Promise<void> {
    await this.runMutation(async () => {
      let foodId = this.resolvedImports.get(input.foodId) ?? input.foodId;
      let food = await this.dependencies.repositories.foods.getById(foodId);
      const pending = this.pendingImports.get(foodId);
      if (!food && pending) {
        if (pending.macroReviewRequired) throw new Error("Review and correct this food's macros before logging it");
        const existingFood = await findExistingMeasureRefresh(
          this.dependencies.repositories.foods,
          pending,
          pending.candidate.basis.unit,
        );
        food = await saveImportedFood(
          {
            foods: this.dependencies.repositories.foods,
            clock: this.dependencies.clock,
            ids: this.dependencies.ids,
          },
          {
            draft: pending,
            finalNutrients: pending.proposedNutrients,
            userReviewedMacros: false,
            existingFood,
          },
        );
        foodId = food.id;
        this.resolvedImports.set(input.foodId, food.id);
        this.pendingImports.delete(input.foodId);
        this.detailedImports.delete(input.foodId);
        food = await this.dependencies.repositories.foods.getById(foodId);
      }
      if (!food) throw new Error("Food not found");
      const portion = portionFromLogQuantity(food, input.quantity);
      const plan = await requireOpenPlan(this.dependencies.repositories.weekPlans);
      const state = checkInStateOn(plan, this.dependencies.clock.today());
      await logFoodUseCase(
        {
          foods: this.dependencies.repositories.foods,
          diary: this.dependencies.repositories.diary,
          weekPlans: this.dependencies.repositories.weekPlans,
          clock: this.dependencies.clock,
          ids: this.dependencies.ids,
        },
        {
          foodId,
          date: input.date,
          meal: input.meal,
          portion,
          intent: state === "overdue_locked" ? "check_in_backfill" : "normal",
        },
      );
    });
  }

  async logRecipe(input: LogRecipeInput): Promise<void> {
    await this.runMutation(async () => {
      const recipe = await this.dependencies.repositories.recipes.getById(input.recipeId);
      if (!recipe) throw new Error("Recipe not found");
      const servings = servingsFromRecipeLogQuantity(recipe, input.quantity);
      const plan = await requireOpenPlan(this.dependencies.repositories.weekPlans);
      const state = checkInStateOn(plan, this.dependencies.clock.today());
      await logRecipeUseCase(
        {
          recipes: this.dependencies.repositories.recipes,
          diary: this.dependencies.repositories.diary,
          weekPlans: this.dependencies.repositories.weekPlans,
          clock: this.dependencies.clock,
          ids: this.dependencies.ids,
        },
        {
          recipeId: input.recipeId,
          servings,
          date: input.date,
          meal: input.meal,
          intent: state === "overdue_locked" ? "check_in_backfill" : "normal",
        },
      );
    });
  }

  async updateDiaryEntry(input: UpdateDiaryEntryInput): Promise<void> {
    await this.runMutation(async () => {
      const entry = await this.dependencies.repositories.diary.getById(input.entryId);
      if (!entry) throw new Error("Diary entry not found");
      await updateDiaryEntryUseCase(
        {
          diary: this.dependencies.repositories.diary,
          weekPlans: this.dependencies.repositories.weekPlans,
          clock: this.dependencies.clock,
        },
        {
          entryId: input.entryId,
          date: input.date,
          meal: input.meal,
          portion: portionFromDiaryLogQuantity(entry, input.quantity),
        },
      );
    });
  }

  async deleteDiaryEntry(entryId: string): Promise<void> {
    await this.runMutation(async () => {
      await deleteDiaryEntryUseCase(
        {
          diary: this.dependencies.repositories.diary,
          weekPlans: this.dependencies.repositories.weekPlans,
          clock: this.dependencies.clock,
        },
        entryId,
      );
    });
  }

  async copyMeal(input: CopyMealInput): Promise<number> {
    return this.runMutation(async () => {
      const plan = await requireOpenPlan(this.dependencies.repositories.weekPlans);
      const state = checkInStateOn(plan, this.dependencies.clock.today());
      const copies = await copyMealUseCase(
        {
          diary: this.dependencies.repositories.diary,
          weekPlans: this.dependencies.repositories.weekPlans,
          transaction: this.dependencies.repositories.transaction,
          clock: this.dependencies.clock,
          ids: this.dependencies.ids,
        },
        {
          ...input,
          intent: state === "overdue_locked" ? "check_in_backfill" : "normal",
        },
      );
      return copies.length;
    });
  }

  async saveWeight(input: { date: string; weightKg: number }): Promise<void> {
    await this.runMutation(async () => {
      const plan = await requireOpenPlan(this.dependencies.repositories.weekPlans);
      const today = this.dependencies.clock.today();
      const state = checkInStateOn(plan, today);
      const isCheckInMeasurement = input.date === today && (state === "due" || state === "overdue_locked");
      await logWeightUseCase(
        {
          weights: this.dependencies.repositories.weights,
          weekPlans: this.dependencies.repositories.weekPlans,
          clock: this.dependencies.clock,
          ids: this.dependencies.ids,
        },
        {
          date: input.date,
          kilograms: input.weightKg,
          intent: isCheckInMeasurement
            ? "check_in_measurement"
            : state === "overdue_locked"
              ? "check_in_backfill"
              : "normal",
        },
      );
    });
  }

  async saveWeekAllocations(allocations: Array<{ date: string; calories: number; isLocked: boolean }>): Promise<void> {
    await this.runMutation(async () => {
      const replacements = Object.fromEntries(allocations.map((allocation) => [allocation.date, {
        calories: allocation.calories,
        isLocked: allocation.isLocked,
      }]));
      await replaceWeekAllocations(
        { weekPlans: this.dependencies.repositories.weekPlans, clock: this.dependencies.clock },
        replacements,
      );
    });
  }

  async previewCheckIn(input: PreviewCheckInInput): Promise<void> {
    this.previewQuestionnaire = questionnaireFromInput(input);
    this.preparedCheckIn = await prepareCheckIn(this.checkInDependencies(), this.previewQuestionnaire);
    await this.refresh();
  }

  async completeCheckIn(input: CompleteCheckInInput): Promise<void> {
    const questionnaire = questionnaireFromInput(input);
    await this.runMutation(async () => {
      const openPlan = await requireOpenPlan(this.dependencies.repositories.weekPlans);
      await completeCheckInUseCase(
        {
          ...this.checkInDependencies(),
          transaction: this.dependencies.repositories.transaction,
          ids: this.dependencies.ids,
        },
        {
          expectedPlanId: openPlan.id,
          questionnaire,
          acceptedBaselineCalories: Math.round(input.acceptedBaselineCalories),
          acceptedDailyProteinGrams: Math.round(input.acceptedProteinGrams),
        },
      );
      this.previewQuestionnaire = undefined;
      this.preparedCheckIn = undefined;
    });

    if (this.dependencies.authorization?.hasValidToken() && this.dependencies.remoteBackup) {
      try {
        await this.backupNow();
      } catch {
        // The completed check-in remains authoritative; backup state stays pending.
      }
    }
  }

  async saveGoalSettings(settings: GoalSettings): Promise<void> {
    await this.runMutation(async () => {
      const today = this.dependencies.clock.today();
      const now = this.dependencies.clock.now();
      const storedSettings = await this.dependencies.configurationRecords.getSettings();
      if (!storedSettings) throw new Error("Complete setup before changing goals");
      if (settings.checkInDay !== storedSettings.nominatedCheckInWeekday) {
        throw new Error("The check-in day is fixed for an active plan");
      }
      const activeGoal = await this.dependencies.repositories.goals.getActive(today);
      const goal = goalFromSettings(
        settings,
        this.dependencies.ids.next(),
        today,
        nextGoalRevisionTimestamp(now, activeGoal?.createdAt),
      );
      const configuration = withRemainderSplit(await this.dependencies.repositories.configuration.get(), settings);
      await this.dependencies.database.runUserDataTransaction(async () => {
        await this.dependencies.repositories.goals.save(goal);
        await this.dependencies.repositories.configuration.save(configuration);
      });
    });
  }

  async saveNutrientGoals(goals: NutrientGoal[]): Promise<void> {
    await this.runMutation(async () => {
      const normalizedGoals = normalizeNutrientGoalInputs(goals);
      const current = await this.dependencies.repositories.nutrientTargets.list();
      const nextKeys = new Set(normalizedGoals.map((goal) => goal.nutrientKey));
      const nextByKey = new Map(normalizedGoals.map((goal) => [goal.nutrientKey, goal]));
      const occupiedKeys = new Set([...current.map((goal) => goal.nutrientKey), ...nextKeys]);
      await this.dependencies.database.runUserDataTransaction(async () => {
        for (const existing of current) {
          const normalized = normalizeTrackedNutrientTarget(existing);
          // Unknown and non-convertible legacy goals remain in the backup even
          // though new goals are restricted to the fixed catalog.
          if (!normalized) {
            const definition = getNutrientDefinition(existing.nutrientKey);
            if (definition && nextKeys.has(definition.id)) {
              const legacyKey = uniqueLegacyGoalKey(`${definition.id}-${existing.unit.toLocaleLowerCase()}`, occupiedKeys);
              await this.dependencies.repositories.nutrientTargets.save(createDailyNutrientTarget({
                ...existing,
                nutrientKey: legacyKey,
                displayName: existing.displayName ?? `${definition.name} (legacy ${existing.unit})`,
              }));
              await this.dependencies.repositories.nutrientTargets.delete(existing.nutrientKey);
              occupiedKeys.add(legacyKey);
            }
            continue;
          }
          if (normalized.nutrientKey !== existing.nutrientKey) {
            const replacement = nextByKey.get(normalized.nutrientKey);
            if (replacement && !sameNutrientTargetAmount(normalized, replacement)) {
              const legacyKey = uniqueLegacyGoalKey(`legacy-conflict-${existing.nutrientKey}`, occupiedKeys);
              const originalName = existing.displayName ?? normalized.displayName ?? existing.nutrientKey;
              await this.dependencies.repositories.nutrientTargets.save(createDailyNutrientTarget({
                ...existing,
                nutrientKey: legacyKey,
                displayName: `Legacy conflict: ${originalName}`.slice(0, NUTRIENT_NAME_MAX_LENGTH),
              }));
              occupiedKeys.add(legacyKey);
            }
            await this.dependencies.repositories.nutrientTargets.delete(existing.nutrientKey);
          } else if (!nextKeys.has(normalized.nutrientKey)) {
            await this.dependencies.repositories.nutrientTargets.delete(existing.nutrientKey);
          }
        }
        for (const goal of normalizedGoals) {
          await this.dependencies.repositories.nutrientTargets.save(goal);
        }
      });
    });
  }

  async connectGoogleDrive(): Promise<void> {
    const authorization = this.dependencies.authorization;
    if (!authorization || !this.dependencies.remoteBackup) {
      throw new Error("Google Drive backup is not configured for this deployment");
    }
    await this.withBackupBusy(async () => {
      await authorization.connect();
      const settings = await this.dependencies.configurationRecords.getSettings();
      if (settings) {
        await this.dependencies.configurationRecords.putSettings({
          ...settings,
          googleDriveBackupEnabled: true,
          updatedAt: this.dependencies.clock.now(),
        });
      }
      await this.refresh();
    });
  }

  async disconnectGoogleDrive(): Promise<void> {
    this.dependencies.authorization?.disconnect();
    const settings = await this.dependencies.configurationRecords.getSettings();
    if (settings) {
      await this.dependencies.configurationRecords.putSettings({
        ...settings,
        googleDriveBackupEnabled: false,
        updatedAt: this.dependencies.clock.now(),
      });
    }
    await this.refresh();
  }

  async backupNow(): Promise<void> {
    if (!this.dependencies.remoteBackup || !this.dependencies.authorization?.hasValidToken()) {
      throw new Error("Connect Google Drive before backing up");
    }
    await this.withBackupBusy(async () => {
      await createRemoteBackup({ local: this.dependencies.localBackup, remote: this.dependencies.remoteBackup! });
      await this.refresh();
    });
  }

  async restoreLatestBackup(): Promise<RestoreResult> {
    if (!this.dependencies.remoteBackup || !this.dependencies.authorization?.hasValidToken()) {
      throw new Error("Connect Google Drive before restoring");
    }
    return this.withBackupBusy(async () => {
      const document = await restoreLatestRemoteBackup({
        local: this.dependencies.localBackup,
        remote: this.dependencies.remoteBackup!,
      });
      this.pendingImports.clear();
      this.detailedImports.clear();
      this.resolvedImports.clear();
      this.previewQuestionnaire = undefined;
      this.preparedCheckIn = undefined;
      await this.refresh();
      return {
        restoredFrom: new Date(document.createdAt).toLocaleString(),
        recordCount: countBackupRecords(document.data),
      };
    });
  }

  private checkInDependencies() {
    return {
      clock: this.dependencies.clock,
      weights: this.dependencies.repositories.weights,
      weekPlans: this.dependencies.repositories.weekPlans,
      checkIns: this.dependencies.repositories.checkIns,
      goals: this.dependencies.repositories.goals,
      configuration: this.dependencies.repositories.configuration,
      onboardingReference: this.dependencies.repositories.onboardingReference,
    };
  }

  private async runMutation<T>(work: () => Promise<T>): Promise<T> {
    return this.dependencies.coordinator.exclusive(USER_DATA_WRITE_LOCK, async () => {
      const result = await work();
      this.dependencies.coordinator.publish("data-changed");
      await this.refresh();
      return result;
    });
  }

  private async withBackupBusy<T>(work: () => Promise<T>): Promise<T> {
    if (this.backupBusy) throw new Error("A backup operation is already running");
    this.backupBusy = true;
    this.emitSnapshot({ ...this.snapshot, backup: { ...this.snapshot.backup, isBusy: true } });
    try {
      return await work();
    } finally {
      this.backupBusy = false;
      await this.refresh();
    }
  }

  private readonly handleConnectivityChange = (): void => {
    this.emitSnapshot({ ...this.snapshot, isOnline: onlineStatus() });
  };

  private async refresh(): Promise<void> {
    const today = this.dependencies.clock.today();
    const [plan, settings, profile, goal, storedNutrientTargets, backupState] = await Promise.all([
      this.dependencies.repositories.weekPlans.getOpen(),
      this.dependencies.configurationRecords.getSettings(),
      this.dependencies.configurationRecords.getProfile(),
      this.dependencies.repositories.goals.getActive(today),
      this.dependencies.repositories.nutrientTargets.list(),
      this.dependencies.database.backupState.get("google-drive"),
    ]);
    const nutrientTargets = normalizeTrackedNutrientTargets(storedNutrientTargets);

    if (!plan || !settings || !profile || !goal) {
      const empty = createEmptySnapshot(today, onlineStatus());
      empty.backup = {
        driveConnected: this.dependencies.authorization?.hasValidToken() ?? false,
        lastBackupAt: backupState?.lastBackupAt,
        backupPending: backupState?.pending ?? false,
        isBusy: this.backupBusy,
      };
      this.emitSnapshot(empty);
      return;
    }

    const [week, weight] = await Promise.all([
      this.buildWeekSummary(plan, nutrientTargets, today),
      this.buildWeightSummary(today, settings.weightUnit),
    ]);
    const goalSettings = await this.buildGoalSettings(goal, settings.nominatedCheckInWeekday);
    const checkIn = await this.buildCheckInSummary(plan, goal, settings.nominatedCheckInWeekday, today);

    this.emitSnapshot({
      needsOnboarding: false,
      todayDate: today,
      todayLabel: formatLongDate(today),
      isOnline: onlineStatus(),
      week,
      weight,
      checkIn,
      goals: goalSettings,
      nutrientGoals: nutrientTargets.map(toUiNutrientGoal),
      backup: {
        driveConnected: this.dependencies.authorization?.hasValidToken() ?? false,
        lastBackupAt: backupState?.lastBackupAt,
        backupPending: backupState?.pending ?? false,
        isBusy: this.backupBusy,
      },
    });
  }

  private async buildWeekSummary(
    plan: WeekPlan,
    nutrientTargets: readonly DailyNutrientTarget[],
    today: CalendarDate,
  ): Promise<AppSnapshot["week"]> {
    const entries = await this.dependencies.repositories.diary.listBetween(plan.startDate, plan.endDate);
    const entriesByDate = new Map<CalendarDate, DomainDiaryEntry[]>();
    for (const entry of entries) {
      const existing = entriesByDate.get(entry.date) ?? [];
      existing.push(entry);
      entriesByDate.set(entry.date, existing);
    }
    const days: DaySummary[] = plan.allocations.map((allocation) => {
      const dailyEntries = entriesByDate.get(allocation.date) ?? [];
      const summary = summarizeDiary(allocation.date, dailyEntries);
      const progress = calculateDailyPlanProgress(plan, summary, nutrientTargets);
      const fibre = progress.nutrientTargets.find((target) => target.nutrientKey === "fibre");
      const micronutrients = progress.nutrientTargets
        .filter((target) => !["protein", "carbohydrate", "fat", "fibre"].includes(target.nutrientKey))
        .map((target) => ({
          id: target.nutrientKey,
          name: target.displayName ?? nutrientDisplayName(target.nutrientKey),
          amount: target.consumedAmount,
          target: target.targetAmount,
          unit: target.unit,
        }));
      return {
        date: allocation.date,
        shortLabel: formatDate(allocation.date, { weekday: "short" }),
        longLabel: formatDate(allocation.date, { weekday: "short", day: "numeric", month: "short" }),
        isToday: allocation.date === today,
        isFuture: allocation.date > today,
        isLocked: allocation.isLocked,
        caloriesConsumed: Math.round(progress.consumedCalories),
        caloriesTarget: progress.allocatedCalories,
        dailyFloorCalories: calculateDailyCalorieFloor(
          plan.dailyFloorPolicy,
          plan.fixedDailyProteinGrams,
          plan.macroDistribution,
        ),
        proteinConsumedGrams: roundOne(progress.proteinConsumedGrams),
        proteinTargetGrams: progress.macroTargets.proteinGrams,
        carbohydrateConsumedGrams: roundOne(summary.nutrients.macros.carbohydrateGrams),
        carbohydrateTargetGrams: roundOne(progress.macroTargets.carbohydrateGrams),
        fatConsumedGrams: roundOne(summary.nutrients.macros.fatGrams),
        fatTargetGrams: roundOne(progress.macroTargets.fatGrams),
        fibreConsumedGrams: summary.nutrients.fibreGrams === undefined ? undefined : roundOne(summary.nutrients.fibreGrams),
        fibreTargetGrams: fibre?.targetAmount,
        micronutrients,
        diaryEntries: dailyEntries.map(toUiDiaryEntry),
      };
    });
    const carbFatTotal = plan.macroDistribution.carbohydratePercent + plan.macroDistribution.fatPercent;
    return {
      startsOn: plan.startDate,
      endsOn: plan.endDate,
      baselineDailyCalories: plan.baselineDailyCalories,
      weeklyBudgetCalories: plan.weeklyCalorieBudget,
      fixedDailyProteinGrams: plan.fixedDailyProteinGrams,
      carbohydrateShareOfRemainder: plan.macroDistribution.carbohydratePercent / carbFatTotal,
      fatShareOfRemainder: plan.macroDistribution.fatPercent / carbFatTotal,
      days,
    };
  }

  private async buildWeightSummary(today: CalendarDate, unit: "kg" | "lb"): Promise<AppSnapshot["weight"]> {
    const start = addCalendarDays(today, -29);
    const [series, recentEntries] = await Promise.all([
      getWeightChart(this.dependencies.repositories.weights, start, today),
      this.dependencies.repositories.weights.listBetween(addCalendarDays(today, -120), today),
    ]);
    const factor = unit === "lb" ? 2.2046226218 : 1;
    const points: WeightPoint[] = series
      .filter((point) => point.rawKilograms !== undefined || point.trendKilograms !== undefined)
      .map((point) => ({
        date: point.date,
        label: formatDate(point.date, { day: "numeric", month: "short" }),
        weight: point.rawKilograms === undefined ? undefined : roundOne(point.rawKilograms * factor),
        rollingAverage: point.trendKilograms === undefined ? undefined : roundOne(point.trendKilograms * factor),
      }));
    const latest = [...recentEntries].sort((a, b) => a.date.localeCompare(b.date)).at(-1);
    const trend = fiveCalendarDayWeightTrend(recentEntries, today);
    return {
      unit,
      latest: latest ? roundOne(latest.kilograms * factor) : undefined,
      latestDate: latest?.date,
      rollingAverage: trend ? roundOne(trend.averageKilograms * factor) : undefined,
      rollingAverageSampleSize: trend?.sampleCount ?? 0,
      points,
    };
  }

  private async buildGoalSettings(goal: GoalPhase, checkInDay: number): Promise<GoalSettings> {
    const configuration = await this.dependencies.repositories.configuration.get();
    const split = configuration.macroDistribution;
    const carbFatTotal = split.carbohydratePercent + split.fatPercent;
    return {
      kind: goal.mode,
      weeklyRatePercent: goal.mode === "maintain" ? DEFAULT_GOAL_SETTINGS.weeklyRatePercent : goal.weeklyRatePercent,
      maintenanceGoalWeightKg: goal.mode === "maintain" ? goal.targetWeightKilograms : DEFAULT_GOAL_SETTINGS.maintenanceGoalWeightKg,
      maintenanceTolerancePercent: goal.mode === "maintain" ? goal.tolerancePercent : DEFAULT_GOAL_SETTINGS.maintenanceTolerancePercent,
      checkInDay,
      carbohydrateShareOfRemainder: split.carbohydratePercent / carbFatTotal,
      fatShareOfRemainder: split.fatPercent / carbFatTotal,
    };
  }

  private async buildCheckInSummary(
    plan: WeekPlan,
    goal: GoalPhase,
    nominatedDay: number,
    today: CalendarDate,
  ): Promise<AppSnapshot["checkIn"]> {
    const state = checkInStateOn(plan, today);
    let prepared: PreparedCheckIn | undefined;
    if (state === "due" || state === "overdue_locked") {
      prepared = await prepareCheckIn(this.checkInDependencies(), this.previewQuestionnaire ?? DEFAULT_QUESTIONNAIRE);
      this.preparedCheckIn = prepared;
    } else {
      this.preparedCheckIn = undefined;
    }
    const configuration = prepared?.configuration ?? await this.dependencies.repositories.configuration.get();
    const proposedProtein = prepared?.proteinTarget.gramsPerDay ?? plan.fixedDailyProteinGrams;
    const currentTrend = prepared?.currentTrend;
    const previousTrend = prepared?.previousTrend;
    const measuredWeeklyChange =
      currentTrend && previousTrend
        ? weeklyPercentChange(
            previousTrend.averageKilograms,
            currentTrend.averageKilograms,
            previousTrend.date,
            currentTrend.date,
          )
        : undefined;
    const recommendedBaseline = prepared?.recommendation.recommendedBaselineCalories ?? plan.baselineDailyCalories;
    const minimumRecommended = Math.max(
      configuration.recommendationPolicy.minimumRecommendedCalories,
      calculateDailyCalorieFloor(
        configuration.dailyFloorPolicy,
        proposedProtein,
        configuration.macroDistribution,
      ),
    );
    const status = state === "active" ? "not-due" : state === "due" ? "due" : state === "overdue_locked" ? "overdue" : "complete";
    return {
      status,
      dueDate: plan.checkInDueDate,
      nominatedDayLabel: weekdayName(nominatedDay),
      isLoggingLocked: state === "due" || state === "overdue_locked",
      lockMessage:
        state === "overdue_locked"
          ? "Complete the overdue check-in to begin the current week. You can still correct the closing week first."
          : state === "due"
            ? "Complete today's check-in to begin the new week."
            : undefined,
      allowedBackfillDates: plan.allocations.filter((allocation) => allocation.date <= today).map((allocation) => allocation.date),
      previousBaselineCalories: plan.baselineDailyCalories,
      recommendedBaselineCalories: recommendedBaseline,
      minimumRecommendedCalories: minimumRecommended,
      maximumRecommendedCalories: Math.max(
        configuration.recommendationPolicy.maximumRecommendedCalories,
        minimumRecommended,
        recommendedBaseline,
      ),
      expectedWeeklyChangePercent:
        goal.mode === "maintain" ? undefined : goal.mode === "gain" ? goal.weeklyRatePercent : -goal.weeklyRatePercent,
      measuredWeeklyChangePercent: measuredWeeklyChange,
      trendWeight: currentTrend?.averageKilograms,
      trendWeightSampleSize: currentTrend?.sampleCount ?? 0,
      proposedProteinGrams: proposedProtein,
      dailyFloorPolicy: {
        absoluteMinimumCalories: configuration.dailyFloorPolicy.absoluteMinimumCalories,
        minimumNonProteinCalories: configuration.dailyFloorPolicy.minimumNonProteinCalories,
        minimumFatGrams: configuration.dailyFloorPolicy.minimumFatGrams,
      },
      noWeightData: currentTrend === undefined,
    };
  }

  private emitSnapshot(snapshot: AppSnapshot): void {
    this.snapshot = snapshot;
    this.listeners.forEach((listener) => listener());
  }
}

function goalFromSettings(
  settings: GoalSettings,
  id: string,
  effectiveFrom: CalendarDate,
  createdAt: string,
): GoalPhase {
  if (settings.kind === "maintain") {
    return {
      id,
      mode: "maintain",
      effectiveFrom,
      targetWeightKilograms: settings.maintenanceGoalWeightKg,
      tolerancePercent: settings.maintenanceTolerancePercent,
      createdAt,
    };
  }
  return {
    id,
    mode: settings.kind,
    effectiveFrom,
    weeklyRatePercent: settings.weeklyRatePercent,
    createdAt,
  };
}

function nextGoalRevisionTimestamp(now: string, previousCreatedAt?: string): string {
  if (!previousCreatedAt || now > previousCreatedAt) return now;
  const previousInstant = Date.parse(previousCreatedAt);
  return Number.isNaN(previousInstant) ? now : new Date(previousInstant + 1).toISOString();
}

function withRemainderSplit(
  configuration: Awaited<ReturnType<DomainRepositoryBundle["configuration"]["get"]>>,
  settings: GoalSettings,
) {
  const total = settings.carbohydrateShareOfRemainder + settings.fatShareOfRemainder;
  if (!Number.isFinite(total) || total <= 0) throw new Error("Carbohydrate and fat shares must total 100%");
  const proteinPercent = configuration.macroDistribution.proteinPercent;
  const remainder = 100 - proteinPercent;
  return {
    ...configuration,
    macroDistribution: {
      proteinPercent,
      carbohydratePercent: remainder * (settings.carbohydrateShareOfRemainder / total),
      fatPercent: remainder * (settings.fatShareOfRemainder / total),
    },
  };
}

function nutrientsFromDraft(draft: FoodDraft): NutrientProfile {
  const micronutrients: Record<string, NutrientProfile["micronutrients"][string]> = {};
  for (const nutrient of draft.micronutrients) {
    const definition = getNutrientDefinition(nutrient.id) ?? resolveNutrient(nutrient.id) ?? resolveNutrient(nutrient.name);
    if (!definition) throw new Error(`Unknown vitamin or mineral: ${nutrient.name || nutrient.id}`);
    if (micronutrients[definition.id]) throw new Error(`${definition.name} has been added more than once`);
    micronutrients[definition.id] = amountInPreferredUnit(definition, {
      amount: nutrient.amount,
      unit: normalizeCanonicalNutrientUnit(nutrient.unit),
    });
  }
  return {
    macros: {
      proteinGrams: draft.proteinGrams,
      carbohydrateGrams: draft.carbohydrateGrams,
      fatGrams: draft.fatGrams,
    },
    fibreGrams: draft.fibreGrams,
    micronutrients,
  };
}

function foodResultFromFood(food: Food): FoodSearchResult {
  const sourceLabel = food.provenance.kind === "custom"
    ? "Your foods"
    : food.provenance.kind === "photo-estimate"
      ? "Photo estimate (reviewed)"
      : food.provenance.provider ?? "Imported food";
  return {
    id: food.id,
    kind: "food",
    name: food.name,
    brand: food.brand,
    barcode: food.barcode,
    sourceLabel,
    basisLabel: basisLabel(food.basis.amount, food.basis.unit, food.basis.servingName),
    basisAmount: food.basis.amount,
    basisUnit: food.basis.unit,
    servingName: food.basis.servingName,
    ...(food.measures === undefined ? {} : { measures: food.measures.map((measure) => ({ ...measure })) }),
    nutrition: toUiNutrition(food.nutrients),
    requiresMacroReview: false,
  };
}

function foodResultFromImport(id: string, draft: ImportedFoodDraft, attribution?: string): FoodSearchResult {
  const candidate = draft.candidate;
  return {
    id,
    kind: "food",
    name: candidate.name,
    brand: candidate.brand,
    barcode: candidate.barcode,
    sourceLabel: attribution ?? candidate.provider,
    basisLabel: basisLabel(candidate.basis.amount, candidate.basis.unit, candidate.basis.servingName),
    basisAmount: candidate.basis.amount,
    basisUnit: candidate.basis.unit,
    servingName: candidate.basis.servingName,
    ...(candidate.measures === undefined
      ? {}
      : { measures: candidate.measures.map((measure) => ({ ...measure })) }),
    nutrition: toUiNutrition(draft.proposedNutrients),
    requiresMacroReview: draft.macroReviewRequired,
    providerCalories: candidate.reportedCalories,
  };
}

function recipeResultFromRecipe(recipe: Recipe): FoodSearchResult {
  const nutrients = perServingRecipeNutrients(recipe);
  return {
    id: recipe.id,
    kind: "recipe",
    name: recipe.name,
    sourceLabel: "Your recipes",
    basisLabel: basisLabel(1, "serving", recipe.servingName),
    basisAmount: 1,
    basisUnit: "serving",
    servingName: recipe.servingName,
    nutrition: toUiNutrition(nutrients),
    requiresMacroReview: false,
    ingredientCount: recipe.ingredients.length,
  };
}

function recipeDetailsFromRecipe(recipe: Recipe): RecipeDetails {
  return {
    id: recipe.id,
    name: recipe.name,
    servingCount: recipe.servingCount,
    servingName: recipe.servingName,
    ingredients: recipe.ingredients.map((ingredient) => ({
      id: ingredient.foodId,
      kind: "food",
      name: ingredient.name,
      brand: ingredient.brand,
      sourceLabel: "Saved ingredient",
      basisLabel: basisLabel(ingredient.basis.amount, ingredient.basis.unit, ingredient.basis.servingName),
      basisAmount: ingredient.basis.amount,
      basisUnit: ingredient.basis.unit,
      servingName: ingredient.basis.servingName,
      nutrition: toUiNutrition(ingredient.nutrients),
      requiresMacroReview: false,
      amount: ingredient.portion.amount,
    })),
    nutrition: toUiNutrition(perServingRecipeNutrients(recipe)),
  };
}

function toUiNutrition(nutrients: NutrientProfile): NutritionSummary {
  return {
    calories: caloriesFromNutrients(nutrients),
    proteinGrams: nutrients.macros.proteinGrams,
    carbohydrateGrams: nutrients.macros.carbohydrateGrams,
    fatGrams: nutrients.macros.fatGrams,
    fibreGrams: nutrients.fibreGrams,
    micronutrients: Object.entries(nutrients.micronutrients).map(([id, nutrient]) => ({
      id,
      name: nutrient.name ?? nutrientDisplayName(id),
      amount: nutrient.amount,
      unit: nutrient.unit,
    })),
  };
}

function portionFromLogQuantity(
  food: Food,
  quantity: LogFoodInput["quantity"],
): FoodPortion {
  if (quantity.kind === "amount") {
    requirePositiveLogQuantity(quantity.amount, "Food amount");
    if (quantity.measureId === undefined) return { amount: quantity.amount, unit: food.basis.unit };
    const measure = food.measures?.find((candidate) => candidate.id === quantity.measureId);
    if (!measure) throw new Error("The selected food measure is no longer available");
    const amount = quantity.amount * measure.amount;
    requirePositiveLogQuantity(amount, "Converted food amount");
    return {
      amount,
      unit: food.basis.unit,
      display: { amount: quantity.amount, measureId: measure.id, label: measure.label },
    };
  }

  if (quantity.kind !== "calories") {
    throw new Error("Unsupported food quantity type");
  }

  requirePositiveLogQuantity(quantity.calories, "Target calories");
  const basisCalories = caloriesFromNutrients(food.nutrients);
  if (!Number.isFinite(basisCalories) || basisCalories <= 0) {
    throw new Error("Cannot log this food by calories because it has no calculable calories");
  }

  const amount = food.basis.amount * (quantity.calories / basisCalories);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Calculated food amount must be a finite positive number");
  }
  return { amount, unit: food.basis.unit };
}

function servingsFromRecipeLogQuantity(recipe: Recipe, quantity: LogRecipeInput["quantity"]): number {
  if (quantity.kind === "amount") {
    if (quantity.measureId !== undefined) throw new Error("Recipes do not support food measures");
    requirePositiveLogQuantity(quantity.amount, "Recipe servings");
    return quantity.amount;
  }

  if (quantity.kind !== "calories") throw new Error("Unsupported recipe quantity type");
  requirePositiveLogQuantity(quantity.calories, "Target calories");
  const servingCalories = caloriesFromNutrients(perServingRecipeNutrients(recipe));
  if (!Number.isFinite(servingCalories) || servingCalories <= 0) {
    throw new Error("Cannot log this recipe by calories because it has no calculable calories");
  }
  const servings = quantity.calories / servingCalories;
  requirePositiveLogQuantity(servings, "Calculated recipe servings");
  return servings;
}

function portionFromDiaryLogQuantity(
  entry: DomainDiaryEntry,
  quantity: UpdateDiaryEntryInput["quantity"],
): DomainDiaryEntry["food"]["portion"] {
  if (quantity.kind === "amount") {
    requirePositiveLogQuantity(quantity.amount, "Diary amount");
    if (quantity.measureId === undefined) return { amount: quantity.amount, unit: entry.food.basis.unit };
    const measure = entry.food.measures?.find((candidate) => candidate.id === quantity.measureId);
    if (!measure) throw new Error("The selected food measure is no longer available");
    const amount = quantity.amount * measure.amount;
    requirePositiveLogQuantity(amount, "Converted diary amount");
    return {
      amount,
      unit: entry.food.basis.unit,
      display: { amount: quantity.amount, measureId: measure.id, label: measure.label },
    };
  }
  if (quantity.kind !== "calories") throw new Error("Unsupported diary quantity type");
  requirePositiveLogQuantity(quantity.calories, "Target calories");
  if (!Number.isFinite(entry.food.calories) || entry.food.calories <= 0) {
    throw new Error("Cannot edit this entry by calories because it has no calculable calories");
  }
  const amount = entry.food.portion.amount * (quantity.calories / entry.food.calories);
  requirePositiveLogQuantity(amount, "Calculated diary amount");
  const previousDisplay = entry.food.portion.display;
  const previousMeasure = previousDisplay && entry.food.measures?.find(
    (measure) => measure.id === previousDisplay.measureId,
  );
  return {
    amount,
    unit: entry.food.basis.unit,
    ...(previousDisplay && previousMeasure
      ? {
          display: {
            amount: amount / previousMeasure.amount,
            measureId: previousMeasure.id,
            label: previousMeasure.label,
          },
        }
      : {}),
  };
}

function requirePositiveLogQuantity(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${label} must be a finite positive number`);
  }
}

function toUiDiaryEntry(entry: DomainDiaryEntry): AppSnapshot["week"]["days"][number]["diaryEntries"][number] {
  return {
    id: entry.id,
    date: entry.date,
    meal: normalizeMealName(entry.meal),
    foodName: entry.food.name,
    amountLabel: entry.food.portion.display
      ? measureAmountLabel(entry.food.portion.display.amount, entry.food.portion.display.label)
      : basisLabel(entry.food.portion.amount, entry.food.portion.unit, entry.food.basis.servingName),
    basisAmount: entry.food.basis.amount,
    basisUnit: entry.food.basis.unit,
    servingName: entry.food.basis.servingName,
    ...(entry.food.measures === undefined
      ? {}
      : { measures: entry.food.measures.map((measure) => ({ ...measure })) }),
    portionAmount: entry.food.portion.amount,
    ...(entry.food.portion.display === undefined ? {} : { portionDisplay: { ...entry.food.portion.display } }),
    nutrition: toUiNutrition(entry.food.nutrients),
  };
}

function toUiNutrientGoal(target: DailyNutrientTarget): NutrientGoal {
  return {
    nutrientId: target.nutrientKey,
    name: target.displayName ?? nutrientDisplayName(target.nutrientKey),
    amount: target.amount,
    unit: target.unit,
  };
}

function questionnaireFromInput(input: PreviewCheckInInput | CompleteCheckInInput): CheckInQuestionnaire {
  return {
    loggedEverything: input.loggedEverything === "yes",
    calorieCompliancePercent: input.calorieCompliancePercent,
    notes: input.notes.trim() || undefined,
  };
}

async function requireOpenPlan(repository: DomainRepositoryBundle["weekPlans"]): Promise<WeekPlan> {
  const plan = await repository.getOpen();
  if (!plan) throw new Error("No active week plan exists");
  return plan;
}

function importKey(draft: ImportedFoodDraft): string {
  return `import:${draft.candidate.provider}:${draft.candidate.providerFoodId}`;
}

function isProviderMeasureRefresh(food: Food, draft: ImportedFoodDraft): boolean {
  return food.provenance.kind === "imported"
    && (food.measures?.length ?? 0) === 0
    && (draft.candidate.measures?.length ?? 0) > 0
    && food.provenance.provider === draft.candidate.provider
    && food.provenance.providerFoodId === draft.candidate.providerFoodId;
}

async function findExistingMeasureRefresh(
  repository: DomainRepositoryBundle["foods"],
  draft: ImportedFoodDraft,
  expectedUnit: Food["basis"]["unit"],
): Promise<Food | undefined> {
  if ((draft.candidate.measures?.length ?? 0) === 0) return undefined;
  const food = await repository.findBySourceIdentity(
    draft.candidate.provider,
    draft.candidate.providerFoodId,
  );
  if (!food || !isProviderMeasureRefresh(food, draft)) return undefined;
  return food.basis.unit === draft.candidate.basis.unit && food.basis.unit === expectedUnit
    ? food
    : undefined;
}

function sameProviderFoodId(provider: string, expected: string, actual: string): boolean {
  if (expected === actual) return true;
  if (provider !== "open-food-facts") return false;
  const expectedCanonical = canonicalOpenFoodFactsId(expected);
  const actualCanonical = canonicalOpenFoodFactsId(actual);
  return expectedCanonical !== undefined && expectedCanonical === actualCanonical;
}

function canonicalOpenFoodFactsId(value: string): string | undefined {
  const digits = value.replace(/[\s-]/g, "");
  if (!/^\d{6,14}$/.test(digits)) return undefined;
  const significant = digits.replace(/^0+/, "");
  if (!significant) return undefined;
  if (significant.length <= 8) return significant.padStart(8, "0");
  if (significant.length <= 12) return significant.padStart(13, "0");
  return significant;
}

function deduplicateFoodResults(results: FoodSearchResult[]): FoodSearchResult[] {
  const seen = new Set<string>();
  return results.filter((result) => {
    const kind = result.kind ?? "food";
    const key = result.barcode
      ? `${kind}:barcode:${result.barcode}`
      : `${kind}:${result.name.toLocaleLowerCase()}:${result.brand?.toLocaleLowerCase() ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function basisLabel(amount: number, unit: string, servingName?: string): string {
  if (unit === "serving") return `${amount === 1 ? "1" : formatNumber(amount)} ${servingName ?? "serving"}`;
  return `${formatNumber(amount)} ${unit}`;
}

function measureAmountLabel(amount: number, label: string): string {
  return `${formatNumber(amount)} × ${label}`;
}

function nutrientDisplayName(key: string): string {
  const catalogNutrient = getNutrientDefinition(key) ?? resolveNutrient(key);
  if (catalogNutrient) return catalogNutrient.name;
  return key
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function normalizeNutrientGoalInputs(goals: readonly NutrientGoal[]): DailyNutrientTarget[] {
  const normalized: DailyNutrientTarget[] = [];
  const keys = new Set<string>();
  for (const goal of goals) {
    const target = normalizeTrackedNutrientTarget({
      nutrientKey: goal.nutrientId,
      displayName: goal.name,
      amount: goal.amount,
      unit: normalizeCanonicalNutrientUnit(goal.unit),
    });
    if (!target) throw new Error(`Unknown vitamin or mineral: ${goal.name || goal.nutrientId}`);
    if (keys.has(target.nutrientKey)) throw new Error(`${target.displayName} has been added more than once`);
    keys.add(target.nutrientKey);
    normalized.push(createDailyNutrientTarget(target));
  }
  return normalized;
}

function normalizeTrackedNutrientTargets(targets: readonly DailyNutrientTarget[]): DailyNutrientTarget[] {
  const byKey = new Map<string, DailyNutrientTarget>();
  const ordered = [...targets].sort((left, right) => {
    const leftDefinition = resolveNutrient(left.nutrientKey) ?? resolveNutrient(left.displayName ?? "");
    const rightDefinition = resolveNutrient(right.nutrientKey) ?? resolveNutrient(right.displayName ?? "");
    const leftCanonical = left.nutrientKey === leftDefinition?.id || left.nutrientKey === "fibre";
    const rightCanonical = right.nutrientKey === rightDefinition?.id || right.nutrientKey === "fibre";
    return Number(rightCanonical) - Number(leftCanonical) || left.nutrientKey.localeCompare(right.nutrientKey);
  });
  for (const target of ordered) {
    const normalized = normalizeTrackedNutrientTarget(target);
    if (normalized && !byKey.has(normalized.nutrientKey)) byKey.set(normalized.nutrientKey, normalized);
  }
  return [...byKey.values()];
}

function normalizeTrackedNutrientTarget(target: DailyNutrientTarget): DailyNutrientTarget | undefined {
  const unit = normalizeCanonicalNutrientUnit(target.unit);
  if (!isMassNutrientUnit(unit)) return undefined;
  const key = target.nutrientKey.trim().toLocaleLowerCase();
  const name = target.displayName?.trim().toLocaleLowerCase();
  if (key === "fibre" || key === "fiber" || name === "fibre" || name === "fiber") {
    return {
      nutrientKey: "fibre",
      displayName: "Fibre",
      amount: amountInPreferredMassUnit(target.amount, unit, "g"),
      unit: "g",
    };
  }
  const definition = resolveNutrient(target.nutrientKey) ?? resolveNutrient(target.displayName ?? "");
  if (!definition) return undefined;
  const amount = amountInPreferredUnit(definition, { amount: target.amount, unit });
  return {
    nutrientKey: definition.id,
    displayName: definition.name,
    amount: amount.amount,
    unit: definition.unit,
  };
}

function uniqueLegacyGoalKey(base: string, occupiedKeys: ReadonlySet<string>): string {
  const limitedBase = base.slice(0, NUTRIENT_KEY_MAX_LENGTH);
  if (!occupiedKeys.has(limitedBase)) return limitedBase;
  let counter = 2;
  let key = limitedBase;
  do {
    const suffix = `-${counter++}`;
    key = `${limitedBase.slice(0, NUTRIENT_KEY_MAX_LENGTH - suffix.length)}${suffix}`;
  } while (occupiedKeys.has(key));
  return key;
}

function sameNutrientTargetAmount(left: DailyNutrientTarget, right: DailyNutrientTarget): boolean {
  if (left.unit !== right.unit) return false;
  const tolerance = Math.max(1e-9, Math.max(Math.abs(left.amount), Math.abs(right.amount)) * 1e-9);
  return Math.abs(left.amount - right.amount) <= tolerance;
}

function amountInPreferredMassUnit(
  amount: number,
  fromUnit: "g" | "mg" | "mcg",
  toUnit: "g" | "mg" | "mcg",
): number {
  const micrograms = fromUnit === "g" ? amount * 1_000_000 : fromUnit === "mg" ? amount * 1_000 : amount;
  return toUnit === "g" ? micrograms / 1_000_000 : toUnit === "mg" ? micrograms / 1_000 : micrograms;
}

function formatDate(date: string, options: Intl.DateTimeFormatOptions): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, options);
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function roundOne(value: number): number {
  return Math.round(value * 10) / 10;
}

function weeklyPercentChange(previous: number, current: number, previousDate: string, currentDate: string): number | undefined {
  const elapsedDays = (Date.parse(`${currentDate}T00:00:00Z`) - Date.parse(`${previousDate}T00:00:00Z`)) / 86_400_000;
  if (elapsedDays <= 0 || previous <= 0) return undefined;
  return ((current - previous) / previous) * 100 * (7 / elapsedDays);
}

function onlineStatus(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

function countBackupRecords(data: unknown): number {
  if (!data || typeof data !== "object") return 0;
  const counts = (data as { recordCounts?: unknown }).recordCounts;
  if (!counts || typeof counts !== "object") return 0;
  return Object.values(counts).reduce<number>((total, value) => total + (typeof value === "number" ? value : 0), 0);
}
